import api from 'api'
import { CACHE_TAG_ROUTES_STOPS, CACHE_TAG_ROUTE_STOP_LISTS } from 'api/cacheTagTypes'
import { dailyInvalidatesTags } from 'api/route/dailyRoute'

import type {
  DailySlotAssignBody,
  DailySlotDetailsBody,
  DailySlotDetailsResponse,
  DailySlotSequenceBody,
  DailySlotUnassignBody,
  DailySlotUnassignResponse,
  RestoreDailySlotVersionBody,
  RouteSlotCountResponse,
  SlotDailyPreviewResponse,
  SlotDailySequence,
  SyncDailySlotToMasterBody,
  SyncDailyToMasterSlotResponse,
} from 'types/api/route/routeSlotRequests'

interface RouteIdArg {
  routeId: string
}

interface SkipInvalidateArg {
  skipInvalidate?: boolean
}

interface AssignDailySlotsArgs extends RouteIdArg, SkipInvalidateArg {
  data: DailySlotAssignBody
}

interface UnassignDailySlotsArgs extends RouteIdArg, SkipInvalidateArg {
  data: DailySlotUnassignBody
}

interface SetDailySlotSequenceArgs extends RouteIdArg, SkipInvalidateArg {
  data: DailySlotSequenceBody
}

interface GetDailySlotDetailsArgs extends RouteIdArg {
  includeTags?: boolean
  includeExceptions?: boolean
  includeContainers?: boolean
  data: DailySlotDetailsBody
}

interface RestoreDailySlotVersionArgs extends RouteIdArg, SkipInvalidateArg {
  data: RestoreDailySlotVersionBody
}

interface SyncDailySlotToMasterArgs extends RouteIdArg, SkipInvalidateArg {
  data: SyncDailySlotToMasterBody
}

const extendedApi = api.injectEndpoints({
  endpoints: builder => ({
    assignDailySlots: builder.mutation<RouteSlotCountResponse, AssignDailySlotsArgs>({
      query: ({ routeId, data }) => ({
        url: `/api/v3/route/${routeId}/daily/stops/assign`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: dailyInvalidatesTags,
    }),
    unassignDailySlots: builder.mutation<DailySlotUnassignResponse, UnassignDailySlotsArgs>({
      query: ({ routeId, data }) => ({
        url: `/api/v3/route/${routeId}/daily/stops/unassign`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: dailyInvalidatesTags,
    }),
    setDailySlotSequence: builder.mutation<SlotDailySequence, SetDailySlotSequenceArgs>({
      query: ({ routeId, data }) => ({
        url: `/api/v3/route/${routeId}/daily/stops/sequence`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: dailyInvalidatesTags,
    }),
    getDailySlotDetails: builder.query<DailySlotDetailsResponse, GetDailySlotDetailsArgs>({
      query: ({ routeId, includeTags = true, includeExceptions = false, includeContainers = false, data }) => ({
        url: `/api/v3/route/${routeId}/daily/stops/details`,
        method: 'POST',
        params: { includeTags, includeExceptions, includeContainers },
        body: data,
      }),
      providesTags: [CACHE_TAG_ROUTES_STOPS, CACHE_TAG_ROUTE_STOP_LISTS],
    }),
    restoreDailySlotVersion: builder.mutation<RouteSlotCountResponse, RestoreDailySlotVersionArgs>({
      query: ({ routeId, data }) => ({
        url: `/api/v3/route/${routeId}/daily/stops/restore`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: dailyInvalidatesTags,
    }),
    // Preview is read-only — same body as the committing mutation, no cache invalidation.
    previewRestoreDailySlotVersion: builder.mutation<SlotDailyPreviewResponse, RestoreDailySlotVersionArgs>({
      query: ({ routeId, data }) => ({
        url: `/api/v3/route/${routeId}/daily/stops/restore/preview`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [],
    }),
    syncDailySlotToMaster: builder.mutation<SyncDailyToMasterSlotResponse, SyncDailySlotToMasterArgs>({
      query: ({ routeId, data }) => ({
        url: `/api/v3/route/${routeId}/daily/stops/sync-to-master`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: dailyInvalidatesTags,
    }),
  }),
})

export const {
  useAssignDailySlotsMutation,
  useUnassignDailySlotsMutation,
  useSetDailySlotSequenceMutation,
  useLazyGetDailySlotDetailsQuery,
  useRestoreDailySlotVersionMutation,
  usePreviewRestoreDailySlotVersionMutation,
  useSyncDailySlotToMasterMutation,
} = extendedApi
